import { Gap, Modal, Span } from '@salecycle/ui-component-library';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CampaignExitIntentAreaSelector } from './CampaignExitIntentAreaSelector';
import { useTrigger } from './hooks/useTrigger';
import { ExitIntentAreas } from 'sections/onsite-campaigns/types';

interface IProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

const toggleSelectedIntent = (intent: ExitIntentAreas, currentIntents: ExitIntentAreas[]) => {
  if (!currentIntents || !currentIntents.length) {
    return [intent];
  }

  if (!!currentIntents.find(p => p === intent.toString())) {
    currentIntents = currentIntents.filter(p => p !== intent);
  } else {
    currentIntents = [...currentIntents, intent];
  }

  return currentIntents;
};

export function CampaignExitIntentAreaSelectorModal({ isOpen, setIsOpen }: IProps) {
  const { t } = useTranslation(['designs', 'campaigns']);
  const { exitIntentAreas, setExitIntent, selectAllExitIntents } = useTrigger();

  const [exitIntents, setExitIntents] = useState<ExitIntentAreas[]>(exitIntentAreas || []);

  useEffect(() => {
    setExitIntents(exitIntentAreas);
  }, [exitIntentAreas]);

  const onSave = useCallback(() => {
    if (exitIntents.length !== Object.values(ExitIntentAreas).length) {
      setExitIntent(exitIntents);
    } else {
      selectAllExitIntents();
    }
    setIsOpen(false);
  }, [exitIntents, selectAllExitIntents, setExitIntent, setIsOpen]);

  const handleClose = () => {
    setIsOpen(false);
    setExitIntents(exitIntentAreas || []);
  };

  const onExitIntentSet = useCallback(
    (intent: ExitIntentAreas) => {
      setExitIntents(toggleSelectedIntent(intent, exitIntents));
    },
    [exitIntents],
  );

  const selectAllExitIntent = useCallback(() => setExitIntents(Object.values(ExitIntentAreas)), []);
  const unselectAllExitIntent = useCallback(() => setExitIntents([]), []);

  return (
    <Modal
      heading={'Select exit intent hotspots'}
      variation="clean"
      isOpen={isOpen}
      onCloseButtonClicked={handleClose}
      onOkButtonClicked={onSave}
      okButtonLabel="Confirm"
      okButtonDisabled={!exitIntents?.length}
      minWidth={848}
    >
      <Span>
        <p>{t('campaigns:onsite.exitIntentAreaSelectorModal.heading')}</p>
        <p>{t('campaigns:onsite.exitIntentAreaSelectorModal.message')}</p>
        <Gap bottom={34} />
        <CampaignExitIntentAreaSelector
          exitIntents={exitIntents}
          onExitIntentSet={onExitIntentSet}
          selectAllExitIntent={selectAllExitIntent}
          unselectAllExitIntent={unselectAllExitIntent}
        />
      </Span>
    </Modal>
  );
}
